/**
 * Hook to build and manage ViewGrid columns
 *
 * Based on original MyVault useViewGridColumns.tsx patterns.
 * Builds GridColDef array from view column metadata.
 */

import React, {
  useMemo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type {
  GridColDef,
  GridCellParams,
  GridRowModel,
  GridValueSetter,
} from "@mui/x-data-grid";
import {
  Box,
  IconButton,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { getViewRowId } from "../../services/viewService";
import type { ViewColumn, ProcessedViewData } from "../../services/viewService";
import type { FileReference } from "../../types";
import { formatFileSize } from "../../types";
import { buildEditorColumn } from "./editors";
import { useAuthFetch } from "../../../../utils/useAuthFetch";
import { useWorkspaceContext } from "../../../../providers/WorkspaceProvider";
import {
  withCanvasWriteContext,
  type CanvasWriteContext,
} from "../../services/viewService";

/**
 * Parse a date string or Date object into a Date object
 */
function parseDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  const date = new Date(value as string);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Format a Date object as YYYY-MM-DD string
 */
function formatDateToString(date: Date | null): string | null {
  if (!date) return null;
  return date.toISOString().split("T")[0];
}

/**
 * Format a Date object as ISO datetime string
 */
function formatDateTimeToString(date: Date | null): string | null {
  if (!date) return null;
  return date.toISOString();
}

interface UseViewGridColumnsProps {
  data: ProcessedViewData | undefined;
  viewId: string | undefined;
  columnOrder: string[];
  setColumnOrder: (order: string[]) => void;
  onDeleteRow?: (rowId: string) => void;
  isDeleting?: boolean;
  // New row operations
  onSaveNewRow?: () => Promise<void>;
  onCancelNewRow?: () => void;
  isCreating?: boolean;
  // File column support
  focusTableId?: string;
  onRefresh?: () => void;
  writeContext?: CanvasWriteContext;
  allowEdit?: boolean;
}

interface UseViewGridColumnsReturn {
  columns: GridColDef[];
  handleColumnReorder: (newOrder: string[]) => void;
}

/**
 * Format number with space as thousand delimiter.
 *
 * Four-digit integers are left ungrouped, so a year renders as "2007" rather
 * than "2 007". Both SI and Swedish typographic convention omit the separator
 * at four digits, which is why this is a formatting rule rather than a guess
 * about the value.
 *
 * IT IS NOT YEAR DETECTION, AND NOTHING HERE COULD BE. A year and a quantity
 * are both a plain NUMBER column with no format hint, so the data does not say
 * which is which. This makes 2007 render correctly by applying a convention
 * that happens to cover every year; a four-digit quantity is also rendered
 * ungrouped, which the same convention says is correct.
 *
 * Exported for test only.
 */
export function formatNumber(value: unknown): string {
  if (value === null || value === undefined) return "";
  const num = Number(value);
  if (isNaN(num)) return String(value);
  if (Number.isInteger(num) && Math.abs(num) < 10000) {
    // Same locale, grouping off. String(num) would also read correctly here but
    // emits an ASCII hyphen for negatives where sv-SE emits U+2212, so the sign
    // would change shape either side of the four-digit bound.
    return num.toLocaleString("sv-SE", { useGrouping: false });
  }
  return num.toLocaleString("sv-SE"); // Swedish locale uses space as delimiter
}

/**
 * Format JSON value for display
 */
function formatJsonValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
}

/**
 * Check if value is JSON (object or array)
 */
function isJsonValue(value: unknown): boolean {
  return value !== null && typeof value === "object";
}

/**
 * Get stored column order from localStorage
 */
function getStoredColumnOrder(viewId: string): string[] | null {
  try {
    const key = `myvault-column-order-${viewId}`;
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

/**
 * Store column order to localStorage
 */
function setStoredColumnOrder(viewId: string, order: string[]): void {
  try {
    const key = `myvault-column-order-${viewId}`;
    localStorage.setItem(key, JSON.stringify(order));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Find the row ID from a row object
 */
function findRowId(row: GridRowModel): string | null {
  return getViewRowId(row);
}

/**
 * Map ViewColumn type to GridColDef type
 */
function mapColumnType(
  type: string,
): "string" | "number" | "boolean" | "date" | "dateTime" {
  switch (type) {
    case "number":
      return "number";
    case "boolean":
      return "boolean";
    case "date":
      return "date";
    case "dateTime":
      return "dateTime";
    default:
      return "string";
  }
}

/**
 * FileCell - Renders a file column cell with download link or upload button.
 */
export function FileCell({
  value,
  rowId,
  columnField,
  focusTableId,
  viewId,
  writeContext,
  allowEdit,
  authFetch,
  workspaceId,
  onRefresh,
  onUploaded,
}: {
  value: unknown;
  rowId: string;
  columnField: string;
  focusTableId: string;
  viewId: string;
  writeContext?: CanvasWriteContext;
  allowEdit: boolean;
  authFetch: ReturnType<typeof useAuthFetch>;
  workspaceId: string;
  onRefresh?: () => void | Promise<void>;
  onUploaded?: (reference: FileReference | null) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [busy, setBusy] = useState(false);
  const uploadContextEpoch = useRef(0);
  const contextKey = JSON.stringify([
    workspaceId,
    viewId,
    focusTableId,
    rowId,
    columnField,
    writeContext,
    allowEdit,
  ]);
  useLayoutEffect(() => {
    // Invalidate previous completions even when the selection changes A -> B -> A.
    uploadContextEpoch.current += 1;
    setConfirmRemove(false);
    setBusy(false);
    return () => {
      uploadContextEpoch.current += 1;
    };
  }, [contextKey]);

  // Parse the FileReference from cell value
  let fileRef: FileReference | null = null;
  if (value) {
    try {
      if (typeof value === "string") {
        fileRef = JSON.parse(value) as FileReference;
      } else if (typeof value === "object") {
        fileRef = value as FileReference;
      }
    } catch {
      // Not valid JSON -- ignore
    }
  }

  const handleDownload = async () => {
    if (!fileRef) return;
    // Extract just the column name (after the dot if aliased)
    const columnName = columnField.includes(".")
      ? columnField.split(".").pop()!
      : columnField;
    try {
      const result = await authFetch(
        `/api/workspace/myvault/files/${focusTableId}/${rowId}/${columnName}?redirect=false&view_id=${encodeURIComponent(viewId)}`,
        "download file",
        { headers: { "x-workspace-id": workspaceId } },
      );
      if (result?.url) {
        window.open(result.url, "_blank");
      }
    } catch (err) {
      console.error("File download failed:", err);
    }
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!allowEdit || busy) return;
    const file = event.target.files?.[0];
    if (!file) return;
    const columnName = columnField.includes(".")
      ? columnField.split(".").pop()!
      : columnField;
    const startedEpoch = uploadContextEpoch.current;
    setBusy(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const reference = await authFetch(
        withCanvasWriteContext(
          `/api/workspace/myvault/files/${focusTableId}/${rowId}/${columnName}?view_id=${encodeURIComponent(viewId)}`,
          writeContext,
        ),
        "upload file",
        {
          method: "POST",
          body: formData,
          headers: { "x-workspace-id": workspaceId },
        },
      );
      if (startedEpoch !== uploadContextEpoch.current) return;
      onUploaded?.(reference);
      await onRefresh?.();
    } catch (err) {
      console.error("File upload failed:", err);
    }
    // Reset input so the same file can be re-selected
    if (startedEpoch === uploadContextEpoch.current && fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (startedEpoch === uploadContextEpoch.current) setBusy(false);
  };

  const handleRemove = async () => {
    if (!allowEdit || busy) return;
    const startedEpoch = uploadContextEpoch.current;
    setConfirmRemove(false);
    setBusy(true);
    const columnName = columnField.split(".").pop()!;
    try {
      await authFetch(
        withCanvasWriteContext(
          `/api/workspace/myvault/files/${focusTableId}/${rowId}/${columnName}?view_id=${encodeURIComponent(viewId)}`,
          writeContext,
        ),
        "remove file",
        { method: "DELETE", headers: { "x-workspace-id": workspaceId } },
      );
      if (startedEpoch !== uploadContextEpoch.current) return;
      onUploaded?.(null);
      await onRefresh?.();
    } catch (err) {
      console.error("File removal failed:", err);
    } finally {
      if (startedEpoch === uploadContextEpoch.current) setBusy(false);
    }
  };

  const uploadInput = allowEdit ? (
    <input
      ref={fileInputRef}
      type="file"
      hidden
      aria-label="Choose file to upload"
      onChange={handleUpload}
      disabled={busy}
    />
  ) : null;

  if (fileRef && fileRef.filename) {
    return (
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 0.5, width: "100%" }}
      >
        <AttachFileIcon
          sx={{ fontSize: 16, color: "text.secondary", flexShrink: 0 }}
        />
        <Tooltip
          title={`${fileRef.filename} (${formatFileSize(fileRef.size_bytes)})`}
        >
          <Typography
            variant="body2"
            sx={{
              cursor: "pointer",
              color: "primary.main",
              textDecoration: "underline",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flexGrow: 1,
            }}
            onClick={handleDownload}
          >
            {fileRef.filename}
          </Typography>
        </Tooltip>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", flexShrink: 0 }}
        >
          {formatFileSize(fileRef.size_bytes)}
        </Typography>
        <IconButton size="small" onClick={handleDownload} title="Download">
          <DownloadIcon sx={{ fontSize: 16 }} />
        </IconButton>
        {allowEdit && rowId !== "__new_row__" && (
          <>
            {uploadInput}
            <IconButton
              size="small"
              title="Replace file"
              disabled={busy}
              onClick={() => fileInputRef.current?.click()}
            >
              <UploadFileIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton
              size="small"
              title="Remove file"
              disabled={busy}
              onClick={() => setConfirmRemove(true)}
            >
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <Dialog
              open={confirmRemove}
              onClose={() => setConfirmRemove(false)}
            >
              <DialogTitle>Remove file?</DialogTitle>
              <DialogContent>
                {fileRef.filename} will be removed from this record.
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setConfirmRemove(false)}>Cancel</Button>
                <Button color="error" onClick={handleRemove}>
                  Remove
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Box>
    );
  }

  // Empty cell -- show upload button (only for existing rows)
  if (rowId === "__new_row__") {
    return (
      <Typography variant="body2" sx={{ color: "text.disabled" }}>
        Save row first
      </Typography>
    );
  }

  if (!allowEdit) {
    return (
      <Typography variant="body2" sx={{ color: "text.disabled" }}>
        No file
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {uploadInput}
      <IconButton
        size="small"
        onClick={() => fileInputRef.current?.click()}
        title="Upload file"
        disabled={busy}
      >
        <UploadFileIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
}

export function useViewGridColumns({
  data,
  viewId,
  columnOrder,
  setColumnOrder,
  onDeleteRow,
  isDeleting = false,
  onSaveNewRow,
  onCancelNewRow,
  isCreating = false,
  focusTableId,
  onRefresh,
  writeContext,
  allowEdit = true,
}: UseViewGridColumnsProps): UseViewGridColumnsReturn {
  const authFetch = useAuthFetch();
  const { workspace } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";
  // Build base columns from data
  const baseColumns: GridColDef[] = useMemo(() => {
    if (!data?.columns) return [];

    return data.columns.map((col: ViewColumn) => {
      const isNumber = col.type === "number";
      const isIdentifier =
        col.field === "id" ||
        col.field === "_rowId" ||
        col.field.endsWith(".id");

      // Determine editability based on editMode
      const editMode = col.editMode || (col.editable ? "DIRECT" : "READONLY");
      const isEditable = !isIdentifier && editMode !== "READONLY";

      // Check if any row has JSON data for this column
      const hasJsonData = data?.rows?.some((row) =>
        isJsonValue(row[col.field]),
      );

      // Add visual styling for edit modes
      const cellClassName =
        editMode === "REFERENCE"
          ? "reference-edit-cell"
          : editMode === "DIRECT" && !isIdentifier
            ? "direct-edit-cell"
            : "";

      // Get editor configuration for this column
      const editorConfig = isEditable ? buildEditorColumn(col, viewId) : {};

      // File columns: non-editable with custom renderer
      const isFileColumn = col.inputType === "file";
      if (isFileColumn) {
        const fileCol: GridColDef = {
          field: col.field,
          headerName: col.headerName,
          type: "string",
          width: col.width || 200,
          description: col.description,
          editable: false,
          renderCell: (params: GridCellParams) => {
            const rowId = findRowId(params.row as GridRowModel);
            if (!rowId) return null;
            return (
              <FileCell
                value={params.value}
                rowId={String(rowId)}
                columnField={col.field}
                focusTableId={focusTableId || ""}
                viewId={viewId || ""}
                writeContext={writeContext}
                allowEdit={allowEdit}
                authFetch={authFetch}
                workspaceId={workspaceId}
                onRefresh={onRefresh}
              />
            );
          },
        };
        return fileCol;
      }

      // Create valueSetter for date columns (converts Date back to string)
      const dateValueSetter: GridValueSetter<GridRowModel> = (value, row) => {
        const stringValue = formatDateToString(value as Date | null);
        return { ...row, [col.field]: stringValue };
      };

      const dateTimeValueSetter: GridValueSetter<GridRowModel> = (
        value,
        row,
      ) => {
        const stringValue = formatDateTimeToString(value as Date | null);
        return { ...row, [col.field]: stringValue };
      };

      const baseCol: GridColDef = {
        field: col.field,
        headerName: col.headerName,
        type: mapColumnType(col.type),
        width: col.width || 150,
        description: col.description,
        cellClassName,
        editable: isEditable,
        // Apply number formatting
        ...(isNumber && {
          valueFormatter: (value: unknown) => formatNumber(value),
          align: "right" as const,
          headerAlign: "right" as const,
        }),
        // Apply date handling - convert string to Date for display
        ...(col.type === "date" && {
          valueGetter: (value: unknown) => parseDate(value),
          valueSetter: dateValueSetter,
        }),
        // Apply dateTime handling - convert string to Date for display
        ...(col.type === "dateTime" && {
          valueGetter: (value: unknown) => parseDate(value),
          valueSetter: dateTimeValueSetter,
        }),
        // Apply JSON formatting for object/array values
        ...(!isNumber &&
          hasJsonData && {
            valueFormatter: (value: unknown) => formatJsonValue(value),
          }),
        // Apply editor configuration
        ...editorConfig,
      };

      return baseCol;
    });
  }, [
    data?.columns,
    data?.rows,
    viewId,
    focusTableId,
    authFetch,
    workspaceId,
    onRefresh,
    writeContext,
    allowEdit,
  ]);

  // Initialize column order from localStorage or default
  useEffect(() => {
    if (viewId && baseColumns.length > 0 && columnOrder.length === 0) {
      const stored = getStoredColumnOrder(viewId);
      if (stored && stored.length > 0) {
        // Validate stored order contains all current columns
        const currentFields = new Set(baseColumns.map((c) => c.field));
        const validStoredOrder = stored.filter((f) => currentFields.has(f));
        // Add any new columns not in stored order
        const missingFields = baseColumns
          .map((c) => c.field)
          .filter((f) => !validStoredOrder.includes(f));
        setColumnOrder([...validStoredOrder, ...missingFields]);
      } else {
        setColumnOrder(baseColumns.map((c) => c.field));
      }
    }
  }, [viewId, baseColumns, columnOrder.length, setColumnOrder]);

  // Apply column order
  const orderedColumns: GridColDef[] = useMemo(() => {
    if (columnOrder.length === 0) return baseColumns;

    const columnMap = new Map(baseColumns.map((c) => [c.field, c]));
    return columnOrder
      .map((field) => columnMap.get(field))
      .filter((col): col is GridColDef => col !== undefined);
  }, [baseColumns, columnOrder]);

  // Handle column reorder
  const handleColumnReorder = useCallback(
    (newOrder: string[]) => {
      setColumnOrder(newOrder);
      if (viewId) {
        setStoredColumnOrder(viewId, newOrder);
      }
    },
    [viewId, setColumnOrder],
  );

  // Add actions column for delete/save/cancel
  const columns: GridColDef[] = useMemo(() => {
    // Need actions column if we have delete or new row operations
    const needsActionsColumn = onDeleteRow || onSaveNewRow || onCancelNewRow;
    if (!needsActionsColumn) return orderedColumns;

    const actionsColumn: GridColDef = {
      field: "_actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      editable: false,
      renderCell: (params: GridCellParams) => {
        const rowId = findRowId(params.row as GridRowModel);
        if (!rowId) return null;

        // Use Material UI components (not MUI Joy) since this renders inside DataGrid's ThemeProvider
        const DeleteIcon = require("@mui/icons-material/Delete").default;
        const SaveIcon = require("@mui/icons-material/Save").default;
        const CancelIcon = require("@mui/icons-material/Close").default;
        const { IconButton, Box } = require("@mui/material");

        // For new rows, show Save/Cancel buttons
        if (rowId === "__new_row__") {
          return (
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {onSaveNewRow && (
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => onSaveNewRow()}
                  disabled={isCreating}
                  title="Save"
                >
                  <SaveIcon fontSize="small" />
                </IconButton>
              )}
              {onCancelNewRow && (
                <IconButton
                  size="small"
                  onClick={() => onCancelNewRow()}
                  disabled={isCreating}
                  title="Cancel"
                >
                  <CancelIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          );
        }

        // For existing rows, show Delete button
        if (onDeleteRow) {
          return (
            <IconButton
              size="small"
              color="error"
              onClick={() => onDeleteRow(rowId)}
              disabled={isDeleting}
              title="Delete"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          );
        }

        return null;
      },
    };

    return [...orderedColumns, actionsColumn];
  }, [
    orderedColumns,
    onDeleteRow,
    isDeleting,
    onSaveNewRow,
    onCancelNewRow,
    isCreating,
  ]);

  return {
    columns,
    handleColumnReorder,
  };
}
